<template>
  <duration-range
    :model-value="getValue"
    v-bind="options"
    variant="narrow"
    @update:model-value="setValue"
  />
</template>

<script lang="ts" setup>
import { computed, type FunctionalComponent } from 'vue';
import { useVModel, type VueInstance } from '@vueuse/core';
import NumberRange from '@components/utils/inputs/NumberRange.vue';
import { syncRef } from '@/hooks/state';
import DurationRange from "@utils/inputs/DurationRange.vue";

export interface Value {
  from: number;
  to: number;
}

export interface Options {
  label: string;
  placeholderFrom: string;
  placeholderTo: string;
  icon: VueInstance | FunctionalComponent | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformIn?: (value: any) => number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformOut?: (value: number) => any;
}

export interface Props<AllValues = never> {
  modelValue: Record<string, AllValues> & { range: Value };
  filterKey: string;
  options: Options;
}

const emit = defineEmits(['update:modelValue']);
const props = defineProps<Props>();
const value = useVModel(props, 'modelValue', emit, { passive: true });

const transformIn = computed(() => props.options.transformIn ?? (<V>(v: V) => v));
const transformOut = computed(() => props.options.transformOut ?? ((v?: number) => v));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const internalValue = syncRef<Record<string, any>, Value | null>(value, (v) => ({
  from: transformIn.value(v[props.filterKey]?.from),
  to: transformIn.value(v[props.filterKey]?.to),
}));

const getValue = computed(() => internalValue.value);

const setValue = (incomingValue: Value | null) => {
  internalValue.value = incomingValue;
  if (incomingValue) {
    value.value[props.filterKey] = {
      from: transformOut.value(incomingValue.from),
      to: transformOut.value(incomingValue.to),
    };
  }
};
</script>

<style lang="scss" scoped></style>
